"use client";

import * as React from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";

interface AnalyticsProviderProps {
  /** Empty string when PostHog isn't configured — provider becomes a no-op. */
  posthogKey: string;
  /** PostHog ingestion host (e.g. https://us.i.posthog.com). */
  posthogHost: string;
  /** Marketing site builds pass `marketing`; the app + trainer pass `app` etc. */
  app: "marketing" | "app" | "trainer";
  children: React.ReactNode;
}

/**
 * Boots PostHog on the client, only when the key is populated.
 *
 * PostHog handles both product analytics (page views, custom events)
 * AND error capture in one SDK — `capture_exceptions: true` installs
 * window.onerror + unhandledrejection listeners that ship exceptions
 * into the same project, eliminating a separate Sentry account.
 *
 * The provider is intentionally lightweight: no React Context API
 * surface — call sites import `posthog` from "posthog-js" directly
 * for `posthog.capture(...)`. Keeps the contract simple.
 */
export function AnalyticsProvider({
  posthogKey,
  posthogHost,
  app,
  children,
}: AnalyticsProviderProps) {
  // Bootstrap once on mount.
  React.useEffect(() => {
    if (posthogKey && typeof window !== "undefined" && !posthog.__loaded) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        // Manual page-view firing — Next App Router doesn't fire native
        // page-load events on client navigations, so we wire a separate
        // effect (below) to call `posthog.capture('$pageview')` on
        // pathname changes. Disabling autocapture's pageview avoids
        // double-counting.
        capture_pageview: false,
        capture_pageleave: true,
        // Auto-capture unhandled errors and promise rejections.
        // De-minification uses the browser source maps Next emits
        // with `productionBrowserSourceMaps: true`.
        capture_exceptions: true,
        persistence: "localStorage+cookie",
        loaded: (ph) => {
          ph.register({ app });
        },
      });
    }
    // Run-once: keys/hosts can't change at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {posthogKey ? <PostHogPageviews /> : null}
      {children}
    </>
  );
}

/**
 * Manual `$pageview` firing on every App Router navigation. Suspended
 * because `useSearchParams` reads dynamic data — wrapping in Suspense
 * means it doesn't opt the rest of the page out of static rendering.
 */
function PostHogPageviews() {
  return (
    <React.Suspense fallback={null}>
      <PageviewListener />
    </React.Suspense>
  );
}

function PageviewListener() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  React.useEffect(() => {
    if (!pathname) return;
    const url = searchParams?.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;
    posthog.capture("$pageview", { $current_url: window.location.origin + url });
  }, [pathname, searchParams]);

  return null;
}

/**
 * Convenience wrapper for `posthog.capture` that no-ops when PostHog
 * isn't loaded (e.g. dev without a key set). Use from anywhere in a
 * client component.
 */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  if (!posthog.__loaded) return;
  posthog.capture(event, properties);
}

/**
 * Manually report a caught exception to PostHog. Use for errors you
 * handle gracefully but still want surfaced. Unhandled errors are
 * captured automatically by `capture_exceptions`.
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  if (!posthog.__loaded) return;
  posthog.captureException(error, context);
}

/**
 * Identify a logged-in user so subsequent events are tied to them.
 * Call after sign-in / on app boot when we know who's there.
 */
export function identifyUser(
  userId: string,
  traits?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  if (!posthog.__loaded) return;
  posthog.identify(userId, traits);
}

/** Forget the current user on sign-out. */
export function resetUser(): void {
  if (typeof window === "undefined") return;
  if (!posthog.__loaded) return;
  posthog.reset();
}
